import {
  Avatar,
  Box,
  Button,
  Chip,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from "@mui/material"
import { Link as RouterLink } from "react-router-dom"
import {
  ScoreRecord,
  StudentAcademicStatusDTO,
} from "./studentAcademicStatus.dto"
import { formatScoreAgeLong, isScoreStale } from "./scoreRecency"
import { viewAsUser } from "../../account-management/accountManagement"
import { formatProgramStartDate } from "./programLabel"
import { domainToDisplayName } from "@utils/domainUtils"
import appIcons from "@utils/appIcons"
import { store } from "@app/store"
import { showSuccessSnackbar } from "@features/ui/uiSlice"

// The satisfaction health band for a score, so a bare number reads as good or
// bad at a glance: CSAT on a 1–5 scale (≥4 good, 3 watch, ≤2 bad), NPS on a
// 0–10 scale (≥9 promoter, 7–8 passive, ≤6 detractor). Same low ends the
// Attention flags use (CSAT ≤2 / NPS ≤6).
function satisfactionBand(
  kind: "csat" | "nps",
  score: number,
): "success" | "warning" | "error" {
  if (kind === "csat") {
    return score >= 4 ? "success" : score >= 3 ? "warning" : "error"
  }
  return score >= 9 ? "success" : score >= 7 ? "warning" : "error"
}

// A satisfaction score as a colour-banded pill with its SCALE — "NPS 0 · 4w"
// alone sent Mayer asking what it means, so the pill shows "NPS 0/10", a health
// dot, and the age muted beside it. Missing scores still render (muted "—") so
// CSAT is always represented, not silently absent. Stale scores grey out, their
// dot included, and say so in the tooltip.
function SatisfactionStat(props: {
  kind: "csat" | "nps"
  record?: ScoreRecord | null
}) {
  const { kind, record } = props
  const label = kind === "csat" ? "CSAT" : "NPS"
  const max = kind === "csat" ? 5 : 10
  const stale = record ? isScoreStale(record.submittedAt) : false
  const band = record ? satisfactionBand(kind, record.score) : null
  const dotColor = !record
    ? "action.disabled"
    : stale
    ? "text.disabled"
    : `${band}.main`
  const textColor = !record || stale ? "text.disabled" : "text.primary"
  const age = record ? formatScoreAgeLong(record.submittedAt) : ""
  const tooltip = !record
    ? `No ${label} score submitted yet`
    : `${label} ${record.score} of ${max} · submitted ${new Date(
        record.submittedAt,
      ).toLocaleString()}${
        stale ? " · older than the 60-day freshness window" : ""
      }`
  return (
    <Tooltip title={tooltip}>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          height: "24px",
          px: "8px",
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            bgcolor: dotColor,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="body3"
          sx={{ color: textColor, whiteSpace: "nowrap" }}
        >
          {label} {record ? `${record.score}/${max}` : "—"}
          {record && age && (
            <Box component="span" sx={{ color: "text.disabled" }}>
              {` · ${age}`}
            </Box>
          )}
        </Typography>
      </Box>
    </Tooltip>
  )
}

// Identity + one-click actions for the student snapshot drawer: who this is,
// where they study, and the fastest paths to seeing what they see (tab-scoped
// view-as on Campus — students have no LMS access) or their CRM record. The
// campus copy-link variant stays in the row kebab; these are the direct actions.
export const StudentSnapshotHeader = (props: {
  student: StudentAcademicStatusDTO
}) => {
  const { student } = props
  const { contactDetails, registrationDetails, staff, status } = student

  const initials = contactDetails.name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase()

  const copyEmail = async () => {
    await navigator.clipboard.writeText(contactDetails.email)
    store.dispatch(showSuccessSnackbar(`Email · ${contactDetails.name} copied`))
  }

  return (
    <Stack gap="12px" sx={{ px: "24px", pt: "20px", pb: "16px" }}>
      <Stack direction="row" gap="16px" alignItems="center">
        <Avatar sx={{ width: 48, height: 48, fontSize: 18 }}>{initials}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Tooltip title={contactDetails.name} placement="bottom-start">
            <Typography variant="h6" noWrap>
              {contactDetails.name}
            </Typography>
          </Tooltip>
          {/* The tooltip carries the full address — the visible line can
              ellipsize on narrow panels. */}
          <Tooltip
            title={`${contactDetails.email} · click to copy`}
            placement="bottom-start"
          >
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              onClick={copyEmail}
              sx={{ cursor: "pointer" }}
            >
              {contactDetails.email}
            </Typography>
          </Tooltip>
        </Box>
      </Stack>
      {registrationDetails && (
        <Stack direction="row" gap="8px" flexWrap="wrap">
          {/* The identity chips are shortcuts: program chip → the program
              page, class chip → its class page (the same route the grid's
              Enrollment cell links), same tab, stopPropagation so the panel
              doesn't toggle. Hover underline is the affordance. */}
          <Chip
            size="small"
            clickable
            component={RouterLink}
            to={`/program/${registrationDetails.programId}`}
            onClick={(event) => event.stopPropagation()}
            label={`${domainToDisplayName(
              registrationDetails.domain,
            )} · ${formatProgramStartDate(registrationDetails.cohort)}`}
            sx={{ "&:hover .MuiChip-label": { textDecoration: "underline" } }}
          />
          <Chip
            size="small"
            variant="outlined"
            clickable
            component={RouterLink}
            to={`/program/${registrationDetails.programId}/${registrationDetails.classId}`}
            onClick={(event) => event.stopPropagation()}
            label={registrationDetails.className}
            sx={{ "&:hover .MuiChip-label": { textDecoration: "underline" } }}
          />
          {registrationDetails.language && (
            <Chip
              size="small"
              variant="outlined"
              label={registrationDetails.language}
            />
          )}
          {/* The mentor chip stays a plain label: Employees & Faculty keeps
              its search in redux state, not the URL, and its default role
              filter hides mentors — there is no clean route that lands
              filtered on this person, and a link that opens an unfiltered
              (or empty) table would lie. */}
          {staff.mentor && (
            <Chip
              size="small"
              variant="outlined"
              label={`Mentor: ${staff.mentor.name}`}
            />
          )}
        </Stack>
      )}
      {/* Satisfaction on its own labelled row, given real focus (Mayer #51):
          colour-banded pills carrying the scale, so CSAT/NPS read as good or
          bad at a glance instead of a bare grey number. Always shown — a
          missing score renders muted so CSAT is never silently absent. */}
      <Stack direction="row" gap="8px" alignItems="center" flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">
          Satisfaction
        </Typography>
        <SatisfactionStat kind="csat" record={status.satisfaction.csat} />
        <SatisfactionStat kind="nps" record={status.satisfaction.nps} />
      </Stack>
      <Stack direction="row" gap="8px">
        <Tooltip title="Open Campus as this student · new tab, your session stays">
          <Button
            size="small"
            variant="outlined"
            startIcon={
              <SvgIcon
                component={appIcons.eye}
                inheritViewBox
                sx={{ width: 16, height: 16, fill: "none" }}
              />
            }
            onClick={() => viewAsUser(contactDetails.email)}
          >
            View as · Campus
          </Button>
        </Tooltip>
        {/* No "View as · LMS": students have no LMS access, so impersonating
            them there opens a surface they can't use (Mayer). */}
        {contactDetails.hubspotContactId && (
          <Button
            size="small"
            variant="text"
            onClick={() =>
              window.open(
                `https://app.hubspot.com/contacts/9484219/record/0-1/${contactDetails.hubspotContactId}`,
                "_blank",
              )
            }
          >
            HubSpot
          </Button>
        )}
      </Stack>
    </Stack>
  )
}
